import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { Image } from '../../../Image';
import { Ad } from '../../atoms/Ad';
import { breakpointService } from '../../../../domain/services/breakpointService';
import { ReadMore } from '../../molecules/ReadMore';
import { t } from '../../../../domain/services/configService';
import { isEscapeKey } from '../../../../domain/utils/isEscapeKey';
import { getMaxWidthsBySize } from '../../../../domain/utils/getMaxWidthsBySize';
import { IconPlus } from '../../../Icon/IconPlus';
import { RelatedPods } from '../../../Pod/RelatedPods';
import { SmartLink } from '../../../SmartLink';
import { imageResizerService } from '../../../../domain/services/imageResizerService';
import { mimeTypeService } from '../../../../domain/services/mimeTypeService';
import { theme } from '../../../../domain/theme';
import { PhotographersLink } from '../../atoms/PhotographersLink';
import { ContainerFluid } from '../../atoms/ContainerFluid';
import { GridColumn, GridRow } from '../../atoms/Grid';
import { Actionable } from '../../atoms/Actionable';
import { ContentTakeOver } from '../ContentTakeOver';

const getHelmetData = desktopStyleData => (
  <Helmet>
    <meta property="og:image" content={desktopStyleData.url} />
    <meta property="og:image:width" content={desktopStyleData.width} />
    <meta property="og:image:height" content={desktopStyleData.height} />
    <meta
      property="og:image:type"
      content={mimeTypeService.getMimeType(desktopStyleData.url)}
    />
    <meta property="twitter:image" content={desktopStyleData.url} />
    <link rel="canonical" href={window.location.href} />
  </Helmet>
);

const getImageId = imageEntity =>
  imageEntity.entity.mediaImage.url
    .split('/')
    .pop()
    .replace(/\.\w+$/, '');

const getDesktopStyleData = imageEntity =>
  imageResizerService.getResizedImage(
    imageEntity.entity.mediaImage,
    breakpointService.getLargeDesktopBreakpoint().width,
    {
      position: imageEntity.focalPoint,
      quality: imageResizerService.getImageEntityQuality(imageEntity.quality)
    }
  );

const intersectionEntries = {};

const updateQueryStringParameters = (title, values) => {
  const url = new URL(window.location.href);
  Object.keys(values).forEach(name => {
    if (values[name]) {
      url.searchParams.set(name, values[name]);
    } else {
      url.searchParams.delete(name);
    }
  });
  window.history.replaceState({}, title, url.href);
};

const removeQueryStringParameters = title => {
  updateQueryStringParameters(title, {
    image: null,
    gallery: null
  });
};

export const ExpandedGallery = ({
  content,
  isPage,
  closeOverlay,
  galleryId,
  imageEntities
}) => {
  if (!intersectionEntries[content.id]) {
    intersectionEntries[content.id] = {};
  }

  const styles = {
    container: css`
      padding: ${isPage
        ? theme.spacing('xl')
        : theme.spacing.parse('0 $xl $xl $xl')};
      background: #222;
    `,
    gridColumn: css`
      padding-top: ${isPage ? null : theme.spacing('xl')};
    `,
    imageContainer: css`
      margin-bottom: ${theme.spacing('xl')};
    `,
    close: css`
      position: fixed;
      right: 20px;
      top: 5px;
      max-width: 300px;
      z-index: 999;
      text-align: right;

      p {
        display: inline-block;
        ${theme.font.family('regularWeb')};
        letter-spacing: 3px;
        color: #fff;
        text-transform: uppercase;
        font-size: 0.75rem;
        margin-right: 10px;
      }

      svg {
        vertical-align: middle;
      }
    `,
    copy: css`
      ${theme.font.family('regularWeb')};
      font-size: ${theme.font.size('s')};
      color: #999;
      border-left: 1px solid #555;
      padding-left: ${theme.spacing('base')};
      margin: ${theme.spacing.parse(`$xs $xs $xl 0`)};
      p {
        color: #999;
        margin-bottom: ${theme.spacing('base')};
      }
    `,
    copyText: css`
      a {
        color: #fc0;
        text-decoration: underline;
      }
    `,
    footerLink: css`
      margin: 20px 0;
      ${theme.font.family('boldWeb')};
      font-size: ${theme.font.size('xs')};
      letter-spacing: 3px;
      line-height: 1.5;
      text-transform: uppercase;
      color: #fff;
      a {
        color: #fff;
      }
      s
    `
  };

  const [currentIndex, setCurrentIndex] = useState(-1);
  const uid = `${content.id}-${isPage ? 'is-page' : 'is-not-page'}`;
  const containerRef = useRef();
  const itemRefs = [];
  const imageIndexById = {};
  let imageIndex = 0;
  imageEntities.forEach(imageEntity => {
    if (imageEntity.entity?.mediaImage?.url) {
      itemRefs.push(useRef());
      imageIndexById[getImageId(imageEntity)] = imageIndex;
      imageIndex += 1;
    }
  });

  const intersectionCb = entries => {
    // Add new entries to intersectionEntries.
    entries.forEach(entry => {
      intersectionEntries[content.id][entry.target.id] = entry;
    });
    let maxVisiblePixels = 0;
    let visibleEntry;
    // Get the most visible one.
    Object.keys(intersectionEntries[content.id]).forEach(id => {
      const entry = intersectionEntries[content.id][id];
      const visiblePixels = entry.target.offsetHeight * entry.intersectionRatio;
      if (visiblePixels > maxVisiblePixels) {
        maxVisiblePixels = visiblePixels;
        visibleEntry = entry;
      }
    });
    // Get id from visibleEntry
    const visibleEntryId = visibleEntry?.target.getAttribute('id');
    const newCurrentIndex = imageIndexById[visibleEntryId];
    if (isPage && newCurrentIndex === 0) {
      removeQueryStringParameters(document.title);
    } else {
      updateQueryStringParameters(document.title, {
        image: visibleEntryId,
        gallery: galleryId
      });
    }
    setCurrentIndex(newCurrentIndex);
  };

  const handleEscape = e => {
    if (!isPage && isEscapeKey(e)) {
      closeOverlay();
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(intersectionCb, {
      threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
    });
    itemRefs.forEach(ref => observer.observe(ref.current));
    return () => {
      observer.disconnect();
    };
  }, []);

  // Scroll to image
  useEffect(() => {
    const url = new URL(document.location);
    const queryImage = url.searchParams.get('image');
    if (queryImage) {
      document.getElementById(queryImage)?.scrollIntoView();
    }
  }, []);

  useEffect(() => {
    // close modal on esc
    if (closeOverlay) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      removeQueryStringParameters(document.title);
      if (closeOverlay) {
        closeOverlay();
        document.removeEventListener('keydown', handleEscape);
      }
    };
  }, []);

  return (
    <div css={styles.container} ref={containerRef}>
      <ContainerFluid>
        <GridRow gutter={[null, null, null, 'l']}>
          <GridColumn width={[12, 12, 8, 8]} childrenMarginBottom="l">
            <div css={styles.gridColumn}>
              {imageEntities &&
                imageEntities.map((imageEntity, index) => {
                  // Early opt-out if no imageEntity.mediaImage is available
                  if (imageEntity.entity?.mediaImage?.url) {
                    const id = getImageId(imageEntity);
                    return (
                      <div
                        id={id}
                        ref={itemRefs[index]}
                        key={imageEntity.entity.mediaImage.url}
                        data-image-url={imageEntity.entity.mediaImage.url}
                        css={styles.imageContainer}
                      >
                        {currentIndex === index &&
                          getHelmetData(getDesktopStyleData(imageEntity))}
                        <ContentTakeOver imageEntity={imageEntity.entity}>
                          <Image
                            entity={imageEntity.entity}
                            maxWidths={getMaxWidthsBySize('expandedGallery')}
                            addSchema={isPage}
                            loading={isPage && index === 0 ? 'eager' : 'lazy'}
                            quality={isPage ? null : 100}
                          />
                        </ContentTakeOver>
                        <div css={styles.copy}>
                          {breakpointService.isHandheld() ? (
                            <ReadMore
                              text={imageEntity.entity.caption || content.title}
                              maxChars={
                                breakpointService.isMobile() ? 100 : 250
                              }
                            />
                          ) : (
                            <div
                              css={styles.copyText}
                              // eslint-disable-next-line react/no-danger
                              dangerouslySetInnerHTML={{
                                __html: imageEntity.entity.caption
                              }}
                            />
                          )}
                          <PhotographersLink
                            photographers={imageEntity.entity.photographers}
                            color={{ text: '#555', link: '#fff' }}
                          />
                        </div>

                        <Ad
                          id={`gallery_inline__${index}_${uid}`}
                          content={content}
                        />
                      </div>
                    );
                  }
                  return undefined;
                })}
              {content.relatedGalleries?.length > 0 && (
                <>
                  <RelatedPods galleries={content.relatedGalleries} />
                  <span css={styles.footerLink}>
                    <SmartLink to={`/${t('photo-of-the-day-index')}`}>
                      {`${t('View other months')} `}
                      <IconPlus size={12} color="#fc0" />
                    </SmartLink>
                  </span>
                </>
              )}
            </div>
          </GridColumn>
          <GridColumn width={[12, 12, 4, 4]} sticky>
            <div css={styles.gridColumn}>
              <Ad id={`gallery_side__${uid}`} content={content} />
            </div>
          </GridColumn>
        </GridRow>

        {!isPage && (
          <>
            <Actionable
              title={t('view full article')}
              onClick={closeOverlay}
              onKeyPress={handleEscape}
              display="inline-block"
            >
              <span css={styles.footerLink}>
                {t('view full article')} <IconPlus size={12} color="#fc0" />
              </span>
            </Actionable>
            <Ad id={`gallery_inline_last__${uid}`} content={content} />
          </>
        )}
      </ContainerFluid>
    </div>
  );
};

ExpandedGallery.defaultProps = {
  galleryId: undefined,
  closeOverlay: undefined,
  isPage: false
};
ExpandedGallery.propTypes = {
  imageEntities: PropTypes.arrayOf(PropTypes.shape({ entity: PropTypes.any }))
    .isRequired,
  closeOverlay: PropTypes.func,
  content: PropTypes.objectOf(PropTypes.any).isRequired,
  isPage: PropTypes.bool,
  galleryId: PropTypes.number
};
